import React, { useContext, useState, useEffect } from 'react';
import { LendingContext, WizardContext } from 'context';
import { Carousel, Modal, LoaderAnimation } from 'components';
import { useNavService } from 'hooks';
import { routes } from 'router';
import { TealiumService, request } from 'services';
import { applicationDefaults, formatCurrency } from 'utils';
import { PersonalLoan, TickedCheckbox, UntickedCheckbox, OmfLogo, VodaLendOffer } from 'assets';
import './offers.scss';

export const Quotes = () => {
  const { application, saveError } = useContext(LendingContext);
  const { setNext, setNextEnabled, forceUpdate } = useContext(WizardContext);
  const [showWhatDoesTheseMean, setWhatDoesTheseMean] = useState(false);
  const [selectedOffer, setSelectedOffer] = useState(-1);
  const [omfOffer, setOmfOffer] = useState(null);
  const [eligibilityPending, setEligibilityPending] = useState(
    !sessionStorage.getItem('omfEligibilityDone'),
  );
  const navigate = useNavService();

  const offersList = [
    ...(omfOffer ? [{ ...omfOffer, isOmf: true }] : []),
    ...(application?.offers?.matches || []),
  ];

  useEffect(() => {
    TealiumService.onQuotesStart(
      application?.offers?.matches || [],
      application?.offers?.hashId,
    );

    const tryLoad = () => {
      const stored = sessionStorage.getItem('omfOffer');
      if (stored) {
        setOmfOffer(JSON.parse(stored));
        return true;
      }
      return false;
    };

    if (tryLoad()) { return; }

    let polls = 0;
    const interval = setInterval(() => {
      if (tryLoad() || ++polls >= 60) { clearInterval(interval); }
    }, 3000);

    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (!eligibilityPending) { return; }
    const interval = setInterval(() => {
      if (sessionStorage.getItem('omfEligibilityDone')) {
        setEligibilityPending(false);
        clearInterval(interval);
      }
    }, 500);
    return () => clearInterval(interval);
  }, [eligibilityPending]);

  // When OMF offer is added to the top after a /v1/offer card was already selected,
  // shift the index by 1 so the selection stays on the correct card.
  useEffect(() => {
    if (omfOffer) {
      setSelectedOffer(prev => (prev >= 0 ? prev + 1 : prev));
    }
  }, [omfOffer]);

  useEffect(() => {
    const canGoNext = selectedOffer >= 0;
    setNextEnabled(canGoNext);
    setNext(() => canGoNext && save());
    forceUpdate((fresh) => !fresh);
  }, [selectedOffer]);

  async function acceptOffer(selectedOffer) {
    const { hashId, partnerId } = selectedOffer;

    const reqBody = {
      hashid: hashId,
      partnerId: partnerId,
      type: applicationDefaults.applicationMethods.quote,
    };

    const fetchOptions = {
      method: 'POST',
      body: JSON.stringify(reqBody),
    };

    let response = await request('/v1/accept', {
      fetchOptions,
      baseURLOverwrite: null,
      tealiumErrorLogger: null,
      suppressTealiumEventOnError: false,
      showToastOnError: true,
      ignoreErrorCodes: [401],
    });

    return response;
  }

  function save() {
    const offer = offersList[selectedOffer];

    TealiumService.onQuotesEnd(offer, application?.offers?.hashId);

    if (offer.isOmf) {
      navigate.to(routes.otp);
      return;
    }

    // updateApplication(lendingOnboardingSteps.offers_details, {
    //   hashId: application.offers.hashId,
    //   ...offer,
    // });

    acceptOffer({
      hashId: application.offers.hashId,
      ...offer,
    });

    let url = offersList[selectedOffer]?.liveScore?.url || offersList[selectedOffer]?.url;

    if (!offersList[selectedOffer].live || !url) {
      navigate.to(routes.thankYou);
      return;
    }

    const safeUrl = getSafeRedirectUrl(url);
    if (!safeUrl || saveError) {
      navigate.to(routes.thankYou);
      return;
    }

    window.location.href = safeUrl;
  }

  function getSafeRedirectUrl(url) {
    try {
      let candidate = String(url).trim();
      if (!candidate) {
        return null;
      }

      const hasScheme = /^[a-zA-Z][a-zA-Z0-9+.-]*:/.test(candidate);
      if (candidate.startsWith('//')) {
        candidate = `https:${candidate}`;
      } else if (!hasScheme && !candidate.startsWith('/') && !candidate.startsWith('./') && !candidate.startsWith('../')) {
        candidate = `https://${candidate}`;
      }

      const parsed = new URL(candidate, window.location.origin);
      const isSameOrigin = parsed.origin === window.location.origin;
      const isAllowedScheme = parsed.protocol === 'https:';

      return isAllowedScheme || isSameOrigin ? parsed.toString() : null;
    } catch {
      return null;
    }
  }

  function getSafeImageUrl(url) {
    try {
      const candidate = String(url).trim();
      if (!candidate) {
        return '';
      }

      const parsed = new URL(candidate, window.location.origin);
      const isAllowedScheme = parsed.protocol === 'https:' || parsed.protocol === 'http:';
      const isSameOrigin = parsed.origin === window.location.origin;

      return isAllowedScheme || isSameOrigin ? parsed.toString() : '';
    } catch {
      return '';
    }
  }


  function getOmfDisplayAmount(offer) {
    const requested = Number(application.request.loanAmount);
    const min = Number(offer.minimumLoan);
    const max = Number(offer.maximumLoan);
    const amount = requested < min ? min : requested > max ? max : requested;
    return `R ${amount.toLocaleString('en-ZA')}`;
  }

  function toggleGlossary() {
    TealiumService.onLoanGlossary();
    setWhatDoesTheseMean(!showWhatDoesTheseMean);
  }

  function notWhatYoureLookingFor() {
    navigate.to(routes.suggestions);
  }

  const selectOffer = (values) => {
    setSelectedOffer(values);
  };

  return (
    <div className='offer-container'>
      {eligibilityPending && (
        <div className='eligibility-overlay'>
          <LoaderAnimation width={120} height={160} />
        </div>
      )}
      {showWhatDoesTheseMean && (
        <Modal onClose={() => setWhatDoesTheseMean(false)} size='medium'>
          <h2 className='glossary-title'>Loan Glossary</h2>
          <section className='glossary-content'>
            <p className=' glossary-term'>Principal amount:</p>
            <p>The actual amount that will be paid into your bank account</p>

            <p className=' glossary-term'>Annual interest rate:</p>
            <p>The annual interest rate charged on the loan by the lender</p>

            <p className=' glossary-term'>Principal debt:</p>
            <p>The sum of the Principal Amount and the Initiation Fee.</p>

            <p className=' glossary-term'>Initiation fee:</p>
            <p>
              The amount charged upfront on the loan. It usually becomes part of the Principal Debt
            </p>

            <p className=' glossary-term'>Administration fee:</p>
            <p>The monthly service fee charged on the loan</p>

            <p className=' glossary-term'>Monthly instalment:</p>
            <p>
              The monthly amount that is due on your loan. If your loan term is less than one month,
              then this will equal the outstanding balance
            </p>

            <p className=' glossary-term'>Total amount payable:</p>
            <p>
              The sum of the principal amount as well as any fees or charges raised in the normal
              repayment cycle
            </p>
          </section>
        </Modal>
      )}
      <main className='offer-panel'>
        <header>
          <h2>Quotes available to you</h2>
          <p>Choose the personal loan that is right for you</p>
        </header>
        <Carousel hasArrows activeForDesktop>
          {offersList.map((offer, key) => (
            <article key={key} className={`card ${offer.isOmf ? 'omf-offer-card' : ''} ${selectedOffer === key ? 'offer-selected' : ''}`}>
              {offer.isOmf ? (
                <div className='omf-card-logos'>
                  <img src={VodaLendOffer} alt='Vodalend Personal Loans' className='company-logo' />
                  <img src={OmfLogo} alt='Old Mutual Finance' className='omf-logo' />
                </div>
              ) : (
                <img src={getSafeImageUrl(offer.companyLogoUrl)} alt='Company Logo' className='company-logo' />
              )}
              <div className='loan-amount-block'>
                {offer.isOmf && <p className='loan-amount-label'>From</p>}
                <h3>{offer.isOmf ? getOmfDisplayAmount(offer) : formatCurrency(application.request.loanAmount)}</h3>
              </div>
              <img src={PersonalLoan} alt='Personal loan logo' />
              <section className='card-content'>
                {offer.isOmf ? (
                  <>
                    <p>Principle Amount</p>
                    <p className='amount'>{formatCurrency(offer.principal)}</p>

                    <p>Annual Interest Rate</p>
                    <p className='amount'>{`${(offer.interestRate * 100).toFixed(2)}%`}</p>

                    <p>Initiation Fee</p>
                    <p className='amount'>{formatCurrency(offer.initiationFee)}</p>

                    <p>Monthly Instalment</p>
                    <p className='amount'>{formatCurrency(offer.installment)}</p>

                    <p>Loan Term</p>
                    <p className='amount'>{`${offer.loanTerm} months`}</p>

                  </>
                ) : (
                  <>
                    {offer?.liveScore?.LoanAmount && (
                      <>
                        <p>Principle Amount</p>
                        <p className='amount'>{formatCurrency(offer?.liveScore?.LoanAmount)}</p>
                      </>
                    )}
                    {offer?.liveScore?.InterestRate && (
                      <>
                        <p>Annual Interest Rate</p>
                        <p className='amount'>{`${offer?.liveScore?.InterestRate}%`}</p>
                      </>
                    )}
                    {offer?.liveScore?.InitiationFee && (
                      <>
                        <p>Initiation Fee</p>
                        <p className='amount'>{formatCurrency(offer?.liveScore?.InitiationFee)}</p>
                      </>
                    )}
                    {offer?.liveScore?.MonthlyRepayments && (
                      <>
                        <p>Monthly Instalment</p>
                        <p className='amount'>{formatCurrency(offer?.liveScore?.MonthlyRepayments)}</p>
                      </>
                    )}
                    {offer?.liveScore?.AmountPayable && (
                      <>
                        <p>Total Amount Payable</p>
                        <p className='amount'>{formatCurrency(offer?.liveScore?.AmountPayable)}</p>
                      </>
                    )}
                    {offer?.probability && (
                      <>
                        <p>Chance of success</p>
                        <p>{`${offer?.probability}%`}</p>
                      </>
                    )}
                  </>
                )}
              </section>
              <footer>
                <a onClick={toggleGlossary}>What do these mean?</a>

                <label className='checkbox-label'>
                  <input
                    type='checkbox'
                    name='selectOffer'
                    checked={selectedOffer === key}
                    onChange={() => selectOffer(key)}
                    className='is-hidden'
                  />
                  <img
                    src={selectedOffer === key ? TickedCheckbox : UntickedCheckbox}
                    alt='toggle-icon'
                  />
                  <span className='checkbox-text'>Select offer</span>
                </label>
              </footer>
            </article>
          ))}
        </Carousel>
        <footer>
          <a className='teal-anchor' onClick={notWhatYoureLookingFor}>
            <span>Not what you're looking for?</span> ►
          </a>
        </footer>
      </main>
    </div>
  );
};
